import modalActions from "actions/modal";
import CounterpartyContactsForm from "components/cargoOwners/contactsForm";
import { Transition } from "components/shared/column";
import Loader from "components/shared/loader";
import Select from "components/shared/select";
import { dateTimeToView } from "helpers/date";
import {
    documentBusinessTypes,
    documentBusinessTypesEnum,
    featureModuleTypesEnum,
    relationToTypesEnum,
    universalDocumentSendStatuses,
    universalDocumentSendStatusesEnum,
    universalDocumentStatuses,
    universalDocumentSystems,
} from "helpers/enums";
import { paths } from "helpers/paths";
import { connect } from "helpers/redux";
import { enumLabel, formatNumber } from "helpers/system";
import React, { useEffect, useMemo, useState } from "react";
import { CustomScroll } from "react-custom-scroll";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import SignalRConnection from "services/signalRConnection";
import { delItem, getItem, setSendStatus, setStatus } from "sources/books/finance";

const FinancesDocumentsShow = (props) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({});
    const { id } = useParams();

    const getData = () => {
        setLoading(true);
        getItem(id)
            .then((res) => setData(res))
            .then(() => setLoading(false));
    };

    useEffect(() => {
        getData();
        const connection = new SignalRConnection({
            url: "/signalr/documents",
            connectionParams: id,
            actions: [{ name: "Update", action: getData }],
            joinActionName: "Join",
            leaveActionName: "Leave",
        });

        return () => connection.disconnect();
    }, []);

    const backPath = useMemo(() => {
        switch (data.type) {
            case documentBusinessTypesEnum.Contract:
                return paths.financesContract;
            case documentBusinessTypesEnum.Invoice:
                return paths.financesInvoice;
            case documentBusinessTypesEnum.AcceptanceCertificate:
                return paths.financesAcceptanceCertificate;
            case documentBusinessTypesEnum.TaxInvoice:
                return paths.financesAcceptanceCertificateInvoice;
            default:
                return paths.finances;
        }
    }, [data.type]);

    const renderHead = () => {
        const { history } = props;
        return (
            <div className="book-list__head">
                <div className="book-show__close close close--sm" onClick={() => history.push(backPath)} />
                <div className="book-show__title">Информация о документе</div>
            </div>
        );
    };

    const handleDelete = () => {
        setLoading(true);
        return props.modal({
            name: "confirm",
            title: "Удаление",
            text: "Вы действительно хотите удалить документ?",
            onSuccessAction: () => getData(),
            onCloseAction: () => setLoading(false),
            buttons: { confirm: "Да", cancel: "Нет" },
            action: () => delItem({ id }),
        });
    };

    const changeStatusHandle = ({ name, value }) => {
        setLoading(true);
        switch (name) {
            case "status":
                return setStatus({ id, status: value }).then((res) => {
                    if (res) {
                        getData();
                    }
                    setLoading(false);
                });
            case "sendStatus":
                return setSendStatus({ id, status: value }).then((res) => {
                    if (res) {
                        getData();
                    } else {
                        props.modal({
                            name: "default",
                            windowMod: "sm",
                            title: "Заполните контактные данные",
                            CustomBody: CounterpartyContactsForm,
                            bodyData: { counterpartyId: data.counterparty?.id },
                        });
                    }
                    setLoading(false);
                });
        }
        setLoading(false);
    };

    const renderBody = () => {
        const showRelatedObject = !!data.relatedToId && !data.registryId;

        return (
            <div className="pt-3">
                <table className="table table-sm">
                    <tbody>
                        <tr>
                            <td>Номер</td>
                            <td>{data.number}</td>
                        </tr>
                        <tr>
                            <td>Название</td>
                            <td>{data.name}</td>
                        </tr>
                        <tr>
                            <td>Тип документа</td>
                            <td>{enumLabel(documentBusinessTypes, data.type)}</td>
                        </tr>
                        <tr>
                            <td>Дата документа</td>
                            <td>{dateTimeToView(data.date)}</td>
                        </tr>
                        <tr>
                            <td>Статус документа</td>
                            <td>
                                <div className="mw-350">
                                    <Select
                                        inputProps={{ name: "status" }}
                                        value={data.status}
                                        options={universalDocumentStatuses}
                                        onChange={changeStatusHandle}
                                    />
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td>Статус отправки</td>
                            <td>
                                <div className="pb-2">{enumLabel(universalDocumentSendStatuses, data.sendStatus)}</div>
                                <div
                                    className="btn btn-xs btn-outline-primary"
                                    onClick={() =>
                                        changeStatusHandle({
                                            name: "sendStatus",
                                            value: universalDocumentSendStatusesEnum.SentToEmail,
                                        })
                                    }
                                >
                                    Отправить на почту
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td>Система документооборота</td>
                            <td>{enumLabel(universalDocumentSystems, data.system)}</td>
                        </tr>
                        <tr>
                            <td>НДС</td>
                            <td>{data.vat ? `${data.vat}%` : "Без НДС"}</td>
                        </tr>
                        <tr>
                            <td>Стоимость без НДС</td>
                            <td>
                                {formatNumber(data.totalWithVatExcluded)} {data.currency?.displayName}
                            </td>
                        </tr>
                        {showRelatedObject && (
                            <tr>
                                <td>Связанный объект</td>
                                <td>
                                    {data.relatedToType === relationToTypesEnum.Bid ? (
                                        <Link to={paths.bidShow("bid", data.relatedToId)}>
                                            Заявка: {data.relatedToId}
                                        </Link>
                                    ) : data.relatedToType === relationToTypesEnum.DistributionBid ? (
                                        <Link to={paths.distributionBidShow(data.relatedToId)}>
                                            Заказ: {data.relatedToId}
                                        </Link>
                                    ) : (
                                        `${data.relatedToType || ""} ${data.relatedToId || ""}`
                                    )}
                                </td>
                            </tr>
                        )}
                        <tr>
                            <td>Контрагент</td>
                            <td>
                                {data.counterparty && (
                                    <Link to={paths.cargoOwnersShow(data.counterparty.id)}>
                                        {data.counterparty.name}
                                    </Link>
                                )}
                            </td>
                        </tr>
                        <tr>
                            <td>Файлы</td>
                            <td>
                                {(data.files || []).map((item) => (
                                    <a key={item.id} className="d-block" href={item.accessUrlLink} download>
                                        {item.originalFileName}
                                    </a>
                                ))}
                            </td>
                        </tr>
                        <tr>
                            <td>Дата удаления</td>
                            <td>{dateTimeToView(data.deletedAt)}</td>
                        </tr>
                    </tbody>
                </table>
                {!data.deletedAt &&
                    !props.profile.availableFeatureModules.includes(featureModuleTypesEnum.KonturIntegration) && (
                        <div className="btn btn-xs btn-outline-danger" onClick={handleDelete}>
                            Удалить
                        </div>
                    )}
            </div>
        );
    };

    const renderBodyWrap = () => {
        if (loading) {
            return <Loader prefix="absolute-center" />;
        }

        return <CustomScroll heightRelativeToParent="100%">{renderBody()}</CustomScroll>;
    };

    const { match, location, history } = props;
    return (
        <div className="page-content">
            <Transition dir="full" {...{ match, location, history }}>
                <div className="page-book__show book-show">
                    {renderHead()}
                    <div className="book-show__body">{renderBodyWrap()}</div>
                </div>
            </Transition>
        </div>
    );
};

function container(Component) {
    const mapDispatch = (dispatch) => ({
        modal: (params) => dispatch(modalActions.open(params)),
    });

    const mapState = (state) => ({
        profile: state.profile.profile,
    });

    return connect(mapState, mapDispatch)(Component);
}

export default container(FinancesDocumentsShow);
